/**
 * useGoalKnowledge — the React binding for a goal's "Documents & Knowledge
 * Base" capability.
 *
 * Strict layering: pages call THIS hook; the hook calls {@link GoalKnowledgeStore}
 * (the KB API ops) and {@link GoalDocumentStore} (the persisted `goalDocuments`
 * collection); the stores call the api facade. Pages never import a store or the
 * api directly.
 *
 * It owns three flows for one goal:
 *  1. UPLOAD a document to the goal's scoped prefix in the team KB's S3 data
 *     source, then PERSIST a `goalDocument` pointer (ingested = false) so the
 *     goal remembers its documents across sessions.
 *  2. BUILD / UPDATE the knowledge base — StartIngestion on the planner KB so
 *     the uploaded docs become retrievable everywhere — then stamp the goal's
 *     documents ingested = true.
 *  3. ASK Vector about this goal — RetrieveAndGenerate grounded in the KB
 *     (enriched with the goal's identity so it favours the goal's docs), with
 *     citations. The session id is threaded for light conversational continuity.
 *
 * The document list is now PERSISTED (a real `goalDocuments` DocumentStore
 * collection, goalId-indexed) rather than session-local; the answer transcript
 * remains ephemeral.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useActiveRoom } from '../rooms/RoomContext';
import { GoalKnowledgeStore } from './goalKnowledgeStore';
import { GoalDocumentStore } from './goalDocumentStore';
import { buildGoalQuestion } from './goalKnowledge';
import type { GoalKbAnswer } from './goalKnowledgeStore';
import type { GoalDocument } from './goalKnowledge';
import type { Goal } from './types';

export interface UseGoalKnowledgeResult {
  /** The goal's persisted documents (most-recent first). */
  documents: GoalDocument[];
  /** True while the persisted document list is (re)loading. */
  documentsLoading: boolean;
  /** How many of the goal's documents have been ingested into the KB. */
  ingestedCount: number;
  /** True when the goal has at least one ingested document (drives the KB badge). */
  hasIngestedDocuments: boolean;

  /** True while an upload is in flight. */
  uploading: boolean;
  /** Uploads one text document under the goal's scoped prefix and records it. */
  uploadDoc: (fileName: string, content: string) => Promise<void>;

  /** True while a build/update (ingestion) job is being started. */
  building: boolean;
  /** Latest build/update status line, or null. */
  buildStatus: string | null;
  /** Starts an ingestion and marks the goal's docs ingested. */
  buildKnowledgeBase: () => Promise<void>;

  /** True while a grounded answer is generating. */
  asking: boolean;
  /** The latest grounded answer (+ citations), or null. */
  answer: GoalKbAnswer | null;
  /** The question that produced {@link answer}, for display. */
  lastQuestion: string | null;
  /** Inline error from the last ask, or null. */
  askError: string | null;
  /** Asks Vector about this goal, grounded in the KB. */
  ask: (question: string) => Promise<void>;
  /** Clears the current answer + session. */
  clearAnswer: () => void;
}

export function useGoalKnowledge(
  goalId: string | undefined,
  goal: Goal | null,
): UseGoalKnowledgeResult {
  const api = useApi();
  const { activeRoomId } = useActiveRoom();
  const kbStore = useMemo(() => new GoalKnowledgeStore(api), [api]);
  const docStore = useMemo(() => new GoalDocumentStore(api), [api]);

  const [documents, setDocuments] = useState<GoalDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [building, setBuilding] = useState(false);
  const [buildStatus, setBuildStatus] = useState<string | null>(null);

  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState<GoalKbAnswer | null>(null);
  const [lastQuestion, setLastQuestion] = useState<string | null>(null);
  const [askError, setAskError] = useState<string | null>(null);
  const sessionId = answer?.sessionId;

  const refreshDocuments = useCallback(async () => {
    if (!goalId) {
      setDocuments([]);
      return;
    }
    setDocumentsLoading(true);
    try {
      const list = await docStore.listByGoal(goalId);
      setDocuments(list);
    } catch {
      // Non-fatal: the list simply stays empty; upload/build still work.
      setDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  }, [goalId, docStore]);

  useEffect(() => {
    void refreshDocuments();
  }, [refreshDocuments]);

  const uploadDoc = useCallback(
    async (fileName: string, content: string) => {
      if (!goalId || uploading) return;
      setUploading(true);
      try {
        const uploaded = await kbStore.uploadDoc(goalId, fileName, content);
        await docStore.record(goalId, activeRoomId ?? '', {
          title: uploaded.fileName,
          fileName: uploaded.fileName,
          kbObjectKey: uploaded.objectKey,
        });
        await refreshDocuments();
      } finally {
        setUploading(false);
      }
    },
    [goalId, uploading, kbStore, docStore, activeRoomId, refreshDocuments],
  );

  const buildKnowledgeBase = useCallback(async () => {
    if (building) return;
    setBuilding(true);
    setBuildStatus(null);
    try {
      const result = await kbStore.buildKnowledgeBase();
      setBuildStatus(
        `Ingestion ${result.ingestionStatus || 'started'}` +
          (result.ingestionJobId ? ` (job ${result.ingestionJobId})` : ''),
      );
      if (goalId) {
        // Mark this goal's documents as ingested (best-effort — a failure here
        // must not mask the successful ingestion start).
        try {
          await docStore.markGoalIngested(goalId);
        } catch {
          /* leave docs pending; the next build will re-stamp them */
        }
        await refreshDocuments();
      }
    } catch (e) {
      const raw = e instanceof Error ? e.message : 'Ingestion failed to start.';
      setBuildStatus(`⚠️ ${raw}`);
      throw e;
    } finally {
      setBuilding(false);
    }
  }, [building, kbStore, docStore, goalId, refreshDocuments]);

  const ask = useCallback(
    async (question: string) => {
      const q = question.trim();
      if (!q || asking || !goal) return;
      setAsking(true);
      setAskError(null);
      setLastQuestion(q);
      try {
        const enriched = buildGoalQuestion(goal, q);
        const result = await kbStore.ask(enriched, sessionId);
        setAnswer(result);
      } catch (e) {
        setAskError(
          e instanceof Error ? e.message : 'Grounded answer unavailable.',
        );
      } finally {
        setAsking(false);
      }
    },
    [asking, goal, kbStore, sessionId],
  );

  const clearAnswer = useCallback(() => {
    setAnswer(null);
    setLastQuestion(null);
    setAskError(null);
  }, []);

  const ingestedCount = useMemo(
    () => documents.filter((d) => d.ingested).length,
    [documents],
  );

  return {
    documents,
    documentsLoading,
    ingestedCount,
    hasIngestedDocuments: ingestedCount > 0,
    uploading,
    uploadDoc,
    building,
    buildStatus,
    buildKnowledgeBase,
    asking,
    answer,
    lastQuestion,
    askError,
    ask,
    clearAnswer,
  };
}
